import React, { useState, useEffect } from 'react';
import { Plus, Settings, User } from 'lucide-react';
import { useChildProfile, ChildProfile } from '../contexts/ChildProfileContext';
import { useUserProfile } from '../hooks/useUserProfile';
import { useSubscription } from '../hooks/useSubscription';
import { useHeroes } from '../hooks/useHeroes';
import { useTranslation } from '../contexts/TranslationContext';
import { Capacitor } from '@capacitor/core';
import { supabase } from '../lib/supabaseClient';
import ParentalPin from './ParentalPin';
import SubscriptionScreen from './SubscriptionScreen';

interface ChildProfileSelectorProps {
  onSelectChild: (child: ChildProfile) => void;
  onAddChild: () => void;
  onManageSettings: () => void;
}

const ChildProfileSelector: React.FC<ChildProfileSelectorProps> = ({
  onSelectChild,
  onAddChild,
  onManageSettings,
}) => {
  const { children, loading: childrenLoading, canAddMoreChildren } = useChildProfile();
  const { profile, user, authProvider } = useUserProfile();
  const { hasPremiumAccess, loading: subscriptionLoading } = useSubscription(user?.id);
  const { heroes } = useHeroes();
  const { t } = useTranslation();
  const isNative = Capacitor.isNativePlatform();
  const [showSubscriptionInvite, setShowSubscriptionInvite] = useState(false);

  const loading = childrenLoading || subscriptionLoading;

  // Show subscription invite on mount if user is not premium
  // Shows EVERY time user lands on this screen without premium access
  useEffect(() => {
    console.log('🔔 ChildProfileSelector subscription check:', {
      loading,
      userId: user?.id,
      hasPremiumAccess
    });

    if (!loading && user && !hasPremiumAccess) {
      console.log('🔔 Showing subscription invite!');
      // Small delay to let the screen render first
      const timer = setTimeout(() => setShowSubscriptionInvite(true), 500);
      return () => clearTimeout(timer);
    }
  }, [loading, user, hasPremiumAccess]);

  // Get a hero image for a child - uses their selected hero or first available hero
  const getChildAvatar = (child: ChildProfile): string | null => {
    // First try the child's selected avatar hero
    if (child.avatarHeroId) {
      const selectedHero = heroes.find(h => h.id === child.avatarHeroId);
      if (selectedHero?.emoji) return selectedHero.emoji;
    }
    // Fallback to first available hero
    if (heroes.length > 0 && heroes[0].emoji) {
      return heroes[0].emoji;
    }
    return null;
  };

  // Get background gradient class for a child's avatar bubble
  const getChildBackgroundClass = (child: ChildProfile): string => {
    const gradientMap: Record<string, Record<string, string>> = {
      'sky-purple': {
        light: 'from-sky-100 via-blue-200 to-purple-300',
        normal: 'from-sky-200 via-blue-300 to-purple-400',
        dark: 'from-sky-400 via-blue-500 to-purple-600'
      },
      'sunset': {
        light: 'from-orange-100 via-pink-200 to-red-300',
        normal: 'from-orange-200 via-pink-300 to-red-400',
        dark: 'from-orange-400 via-pink-500 to-red-600'
      },
      'ocean': {
        light: 'from-blue-100 via-cyan-200 to-teal-300',
        normal: 'from-blue-200 via-cyan-300 to-teal-400',
        dark: 'from-blue-400 via-cyan-500 to-teal-600'
      },
      'forest': {
        light: 'from-green-100 via-emerald-200 to-lime-300',
        normal: 'from-green-200 via-emerald-300 to-lime-400',
        dark: 'from-green-400 via-emerald-500 to-lime-600'
      },
      'space': {
        light: 'from-purple-200 via-indigo-300 to-gray-800',
        normal: 'from-purple-400 via-indigo-500 to-gray-900',
        dark: 'from-purple-600 via-indigo-700 to-black'
      }
    };

    const gradient = child.backgroundGradient || 'sky-purple';
    const intensity = child.backgroundIntensity || 'normal';

    return gradientMap[gradient]?.[intensity] || gradientMap['sky-purple']['normal'];
  };
  const [showPinVerification, setShowPinVerification] = useState(false);

  // Handle settings click - verify PIN if configured
  const handleSettingsClick = () => {
    if (profile?.parentPinHash) {
      setShowPinVerification(true);
    } else {
      onManageSettings();
    }
  };

  if (loading) {
    return (
      <div className="h-screen-adaptive bg-gradient-to-b from-purple-400 via-pink-300 to-orange-200 flex items-center justify-center safe-area-top safe-area-bottom">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent mx-auto mb-4"></div>
          <p className="text-white text-lg font-medium">{t("profile.loading", "Loading...")}</p>
        </div>
      </div>
    );
  }

  // Subscription screen - fullscreen
  if (showSubscriptionInvite) {
    return (
      <SubscriptionScreen
        onBack={() => setShowSubscriptionInvite(false)}
        onSubscriptionComplete={async (plan) => {
          // In production, this would be handled by RevenueCat/Stripe webhook
          // For now, simulate the subscription creation
          if (!user) return;

          const now = new Date();
          const trialEndsAt = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
          const currentPeriodEnd = plan === 'yearly'
            ? new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000)
            : new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

          const { error } = await supabase
            .from('subscriptions')
            .upsert({
              user_id: user.id,
              status: 'trial',
              tier: 'premium',
              plan: plan,
              trial_ends_at: trialEndsAt.toISOString(),
              current_period_start: now.toISOString(),
              current_period_end: currentPeriodEnd.toISOString(),
              provider: 'stripe',
            }, {
              onConflict: 'user_id'
            });

          if (error) {
            console.error('Error creating subscription:', error);
          }

          setShowSubscriptionInvite(false);
          window.location.reload();
        }}
      />
    );
  }

  // PIN verification modal
  if (showPinVerification) {
    return (
      <ParentalPin
        mode="verify"
        currentPinHash={profile?.parentPinHash}
        userEmail={user?.email}
        authProvider={authProvider}
        onSuccess={() => {
          setShowPinVerification(false);
          onManageSettings();
        }}
        onCancel={() => setShowPinVerification(false)}
        onPinSet={async (pinHash) => {
          // Called when PIN is reset after password verification
          if (!user) return;
          const { error } = await supabase
            .from('user_profiles')
            .update({ parent_pin_hash: pinHash })
            .eq('id', user.id);

          if (error) {
            console.error('Error saving PIN:', error);
            throw error;
          }

          // Refresh to get updated profile
          window.location.reload();
        }}
      />
    );
  }

  return (
    <div className="h-screen-adaptive bg-gradient-to-b from-purple-400 via-pink-300 to-orange-200 flex flex-col safe-area-top safe-area-bottom">
      {/* Logo centré en haut - avec safe area */}
      <div className="absolute left-1/2 transform -translate-x-1/2 z-20 logo-safe-top">
        <img
          src="/logo-white.png"
          alt="LilHero"
          className="h-20 w-auto drop-shadow-lg"
        />
      </div>

      {/* Header - bouton settings à droite */}
      <div className="flex justify-end p-4">
        <button
          onClick={handleSettingsClick}
          className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <Settings size={20} className="text-white" />
        </button>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4">
        <h2 className="text-xl text-white mb-8 font-medium drop-shadow">
          {t("profile.qui_joue_aujourdhui", "Qui joue aujourd'hui ?")}
        </h2>

        {/* Profiles grid */}
        <div className="flex flex-wrap justify-center gap-6 max-w-md">
          {children.map((child) => (
            <button
              key={child.id}
              onClick={() => onSelectChild(child)}
              className="flex flex-col items-center gap-2 group"
            >
              {/* Avatar circle with child's world background */}
              <div className={`w-24 h-24 rounded-full shadow-lg flex items-center justify-center overflow-hidden group-hover:ring-4 group-hover:ring-white/50 transition-all transform group-hover:scale-105 bg-gradient-to-br ${getChildBackgroundClass(child)} border-4 border-white`}>
                {(() => {
                  const avatarUrl = getChildAvatar(child);
                  if (avatarUrl) {
                    return (
                      <img
                        src={avatarUrl}
                        alt={child.name}
                        className="w-full h-full object-contain p-1"
                      />
                    );
                  }
                  // Fallback to initial if no hero image available
                  return (
                    <span className="text-3xl font-bold text-white drop-shadow-lg">
                      {child.name.charAt(0).toUpperCase()}
                    </span>
                  );
                })()}
              </div>
              {/* Name */}
              <span className="text-white font-medium drop-shadow text-center max-w-[100px] truncate">
                {child.name}
              </span>
            </button>
          ))}

          {/* Add new profile button */}
          {canAddMoreChildren && (
            <button
              onClick={onAddChild}
              className="flex flex-col items-center gap-2 group"
            >
              <div className="w-24 h-24 rounded-full bg-white/30 backdrop-blur-sm border-2 border-dashed border-white/50 flex items-center justify-center group-hover:bg-white/40 transition-all transform group-hover:scale-105">
                <Plus size={32} className="text-white" />
              </div>
              <span className="text-white/80 font-medium drop-shadow">
                {t("profile.add", "Add")}
              </span>
            </button>
          )}
        </div>

        {/* Empty state */}
        {children.length === 0 && (
          <div className="text-center mt-8">
            <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center">
              <User size={48} className="text-white/60" />
            </div>
            <p className="text-white/80 mb-4">
              {t("profile.no_child_profile_created", "No child profile created")}
            </p>
            <button
              onClick={onAddChild}
              className="bg-white text-purple-600 px-6 py-3 rounded-full font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
            >
              <Plus size={20} className="inline mr-2" />
              {t("profile.create_a_profile", "Create a profile")}
            </button>
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="p-4 text-center">
        <p className="text-white/60 text-sm">
          {t("profile.appuyez_sur_licone_engrenage_pour_les_pa", "Appuyez sur l'icône engrenage pour les paramètres parentaux")}
        </p>
      </div>

    </div>
  );
};

export default ChildProfileSelector;
